import React from "react";
import { Icon } from "@iconify/react";
import StatusBadge from "./StatusBadge";
import { getLaunchId } from "./automationConfigs";

const formatDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const AutomationTable = ({
  config,
  items,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
}) => {
  const idField = config.idField;

  if (loading) {
    return (
      <div className="automation-table__state">
        Loading {config.labelPlural.toLowerCase()}…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="automation-table__state">
        <Icon icon="mdi:calendar-blank-outline" width="28" />
        <p>
          No {config.labelPlural.toLowerCase()} yet. Use “New” to create one.
        </p>
      </div>
    );
  }

  // Only rows with a real launch id (i.e. a notification/automation record
  // already exists for them) can be selected for the launch/status endpoint.
  const selectableItems = items.filter(
    (item) => getLaunchId(config, item) != null,
  );
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedIds.has(getLaunchId(config, item)));

  return (
    <div className="automation-table__scroll">
      <table className="automation-table">
        <thead>
          <tr>
            <th className="automation-table__checkbox-col">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
                aria-label="Select all"
              />
            </th>
            <th>Task name</th>
            <th>Type</th>
            {config.hasCronFields && <th>Schedule</th>}
            <th>Status</th>
            <th>Launch status</th>
            <th>Last launched</th>
            <th className="automation-table__actions-col">Edit</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const launchInfo = item[config.launchInfoField] || {};
            const rowKey = item[idField];
            const launchId = getLaunchId(config, item);
            return (
              <tr key={rowKey}>
                <td>
                  <input
                    type="checkbox"
                    checked={launchId != null && selectedIds.has(launchId)}
                    disabled={launchId == null}
                    onChange={() => onToggleSelect(launchId)}
                    aria-label={`Select ${item.taskName}`}
                    title={
                      launchId == null
                        ? "No notification/automation record yet"
                        : undefined
                    }
                  />
                </td>
                <td className="automation-table__task-name">{item.taskName}</td>
                <td>{item[config.typeLabelField] || "—"}</td>
                {config.hasCronFields && (
                  <td className="automation-table__mono">
                    {item.cronJobExpression ||
                      `${item.triggerDate || ""} ${item.days ?? ""}`}
                  </td>
                )}
                <td>
                  <StatusBadge value={item.status} />
                </td>
                <td>
                  <StatusBadge value={launchInfo.status} />
                </td>
                <td>{formatDate(launchInfo.recentLaunchDate)}</td>
                <td>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => onEdit(item)}
                    aria-label={`Edit ${item.taskName}`}
                  >
                    <Icon icon="mdi:pencil-outline" width="18" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AutomationTable;
